import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Chip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MentionEditor from '../TextEditor/MentionEditor';
import useTriggerFromApiPollingStore, { getTriggerFromApiPollingStore,
  HTTP_METHOD_GET,
  HTTP_METHOD_POST,
  HTTP_METHOD_PUT,
  HTTP_METHOD_DELETE,
  INTERVAL_TYPE_MINUTES,
  INTERVAL_TYPE_HOURS,
  INTERVAL_TYPE_DAYS,
  RESPONSE_FORMAT_JSON,
  RESPONSE_FORMAT_XML,
  RESPONSE_FORMAT_TEXT,
  DEFAULT_SUCCESS_CODES,
} from './useTriggerFromApiPollingStore';
import { send_component_save_request, send_request } from '../../utils/Request';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import checkKey from '../../utils/CheckKey';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import CustomPaper from '../common/CustomPaper';
import './TriggerFromApiPollingStyle.css';

const HTTP_METHODS = [
  { value: HTTP_METHOD_GET, label: 'GET' },
  { value: HTTP_METHOD_POST, label: 'POST' },
  { value: HTTP_METHOD_PUT, label: 'PUT' },
  { value: HTTP_METHOD_DELETE, label: 'DELETE' },
];

const INTERVAL_TYPES = [
  { value: INTERVAL_TYPE_MINUTES, label: 'Minutes' },
  { value: INTERVAL_TYPE_HOURS, label: 'Hours' },
  { value: INTERVAL_TYPE_DAYS, label: 'Days' },
];

const RESPONSE_FORMATS = [
  { value: RESPONSE_FORMAT_JSON, label: 'JSON' },
  { value: RESPONSE_FORMAT_XML, label: 'XML' },
  { value: RESPONSE_FORMAT_TEXT, label: 'Plain Text' },
];

const COMMON_STATUS_CODES = [
  { code: 200, label: '200 - OK' },
  { code: 201, label: '201 - Created' },
  { code: 202, label: '202 - Accepted' },
  { code: 204, label: '204 - No Content' },
  { code: 206, label: '206 - Partial Content' },
  { code: 301, label: '301 - Moved Permanently' },
  { code: 302, label: '302 - Found' },
  { code: 303, label: '303 - See Other' },
  { code: 304, label: '304 - Not Modified' },
  { code: 307, label: '307 - Temporary Redirect' },
  { code: 308, label: '308 - Permanent Redirect' },
];

const SESSION_ID_PLACEHOLDER = {
  key: 'SESSION_ID',
  description: 'Workflow session ID (generated for each poll)',
  type: 'system',
};

const TriggerFromApiPollingTemplate = (props) => {
  const [loading, setLoading] = useState(true);
  const [checkKeyError, setCheckKeyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);
  const [pollHistory, setPollHistory] = useState([]);
  const [pollHistoryLoading, setPollHistoryLoading] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState(null);
  const [expandedHistoryEntries, setExpandedHistoryEntries] = useState({});

  const store = useMemo(() => getTriggerFromApiPollingStore(props.component_id), [props.component_id]);

  const data = store((s) => s.data);
  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);

  const getEnhancedPlaceholders = useCallback(() => {
    const propsPlaceholders = props.availablePlaceholders || [];
    if (!propsPlaceholders.length || !propsPlaceholders[0]?.placeholders) {
      return [{ placeholders: [SESSION_ID_PLACEHOLDER] }];
    }
    return [
      {
        ...propsPlaceholders[0],
        placeholders: [SESSION_ID_PLACEHOLDER, ...propsPlaceholders[0].placeholders],
      },
    ];
  }, [props.availablePlaceholders]);

  const fetchPollHistory = useCallback(async () => {
    if (!props.component_id) return;
    setPollHistoryLoading(true);
    try {
      const response = await send_request(
        `component-service/trigger_from_api_polling/poll-history/${props.component_id}`,
        '',
        {}
      );
      if (response?.data) {
        setPollHistory(response.data.pollHistory || []);
        setSubscriptionActive(response.data.active || false);
        setLastPolledAt(response.data.lastPolledAt);
      }
    } catch (error) {
      // Poll history not available
    } finally {
      setPollHistoryLoading(false);
    }
  }, [props.component_id]);

  const handleSave = useCallback(() => {
    const lastModified = getCurrentTime();
    const storeData = store.getState().getTemplate().data;
    const saveData = injectMetadataFields({
      componentData: {
        data: storeData,
        name: componentName,
        lastModified: lastModified,
      },
      componentId: props.component_id,
      type: 'trigger_from_api_polling',
      description: componentDescription,
      status,
    });

    send_component_save_request(
      'component-service/trigger_from_api_polling/data',
      saveData,
      '',
      'POST'
    )
      .then((response) => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(saveData);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch((err) => {
        console.error('Error saving component:', err);
        throw err;
      });
  }, [componentName, componentDescription, status, props]);

  // componentDidMount
  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      if (pollHistory.length === 0 && !pollHistoryLoading) {
        fetchPollHistory();
      }
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    const loadData = async () => {
      try {
        const componentResponse = await send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );

        if (componentResponse?.data) {
          const component = componentResponse.data.components?.[0] || componentResponse.data;
          store.getState().setComponentData(component);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(component.description);
          props.onLastModifiedChanged(store.getState().lastModified);
        }

        fetchPollHistory();
      } catch (error) {
        console.error('Error loading component data:', error);
      } finally {
        setLoading(false);
        props.setChildTemplateLoaded(true);
      }
    };

    loadData();
  }, []);

  const handleKeyChange = useCallback((e) => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setOutputPlaceholderKey(placeholderValue);

    const storeData = store.getState().data;
    const value = checkKey(placeholderValue, storeData);
    setCheckKeyError(value[0]);
    setErrorMessage(value[1]);
  }, []);

  const handleEndpointUrlChange = useCallback((value) => {
    store.getState().setEndpointUrl(value);
  }, []);

  const handleHttpMethodChange = useCallback((e) => {
    store.getState().setHttpMethod(e.target.value);
  }, []);

  const handleRequestBodyChange = useCallback((value) => {
    store.getState().setRequestBody(value);
  }, []);

  const handleResponseFormatChange = useCallback((e) => {
    store.getState().setResponseFormat(e.target.value);
  }, []);

  const handleIntervalValueChange = useCallback((e) => {
    const value = parseInt(e.target.value, 10) || 1;
    store.getState().setPollingIntervalValue(Math.max(1, value));
  }, []);

  const handleIntervalTypeChange = useCallback((e) => {
    store.getState().setPollingIntervalType(e.target.value);
  }, []);

  const handleAddHeader = useCallback(() => {
    store.getState().addHeader();
  }, []);

  const handleUpdateHeader = useCallback((index, field, value) => {
    store.getState().updateHeader(index, field, value);
  }, []);

  const handleRemoveHeader = useCallback((index) => {
    store.getState().removeHeader(index);
  }, []);

  const handleAddQueryParam = useCallback(() => {
    store.getState().addQueryParam();
  }, []);

  const handleUpdateQueryParam = useCallback((index, field, value) => {
    store.getState().updateQueryParam(index, field, value);
  }, []);

  const handleRemoveQueryParam = useCallback((index) => {
    store.getState().removeQueryParam(index);
  }, []);

  const handleStatusCodesChange = useCallback((event, newValue) => {
    const codes = newValue.map((item) => (typeof item === 'object' ? item.code : item));
    store.getState().setSuccessStatusCodes(codes);
  }, []);

  const toggleHistoryEntryExpanded = useCallback((index) => {
    setExpandedHistoryEntries((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const copyResponseToClipboard = useCallback((responseBody) => {
    navigator.clipboard.writeText(responseBody);
  }, []);

  const formatResponseBody = useCallback((body) => {
    if (!body) return '';
    try {
      const parsed = typeof body === 'string' ? JSON.parse(body) : body;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof body === 'string' ? body : JSON.stringify(body);
    }
  }, []);

  const formatPollTime = useCallback((timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  if (loading) {
    return <IndividualComponentLoader />;
  }

  const httpMethod = data.httpMethod || HTTP_METHOD_GET;
  const showRequestBody = httpMethod === HTTP_METHOD_POST || httpMethod === HTTP_METHOD_PUT;

  return (
    <CustomPaper className="trigger-api-polling-container">
      <Grid container spacing={3}>
        {/* Polling Status Banner */}
        <Grid item xs={12}>
          {subscriptionActive ? (
            <div className="trigger-api-polling-status-banner active">
              <CheckCircleIcon
                className="status-banner-icon"
                sx={{ color: '#2e7d32', fontSize: 24 }}
              />
              <div className="status-banner-content">
                <span className="status-banner-title">Polling Active</span>
                <span className="status-banner-subtitle">
                  This trigger is actively polling
                  {lastPolledAt && ` (last poll: ${formatPollTime(lastPolledAt)})`}
                </span>
              </div>
            </div>
          ) : (
            <div className="trigger-api-polling-status-banner inactive">
              <ErrorIcon className="status-banner-icon" sx={{ color: '#f57c00', fontSize: 24 }} />
              <div className="status-banner-content">
                <span className="status-banner-title">Polling Inactive</span>
                <span className="status-banner-subtitle">
                  Polling will start when this workflow is published
                </span>
              </div>
            </div>
          )}
        </Grid>

        {/* Endpoint URL Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <Typography className="trigger-api-polling-label">API Endpoint URL</Typography>
          <Typography className="trigger-api-polling-helper-text">
            Enter the URL to poll. Type "$" to insert placeholders or secrets. Use $
            {'{SESSION_ID}'} to include the workflow session ID in your request.
          </Typography>
          <MentionEditor
            availablePlaceholders={getEnhancedPlaceholders()}
            text={data.endpointUrl || ''}
            placeholder="https://api.example.com/endpoint"
            onChange={handleEndpointUrlChange}
            onFocus={() => {}}
            error={false}
            className="trigger-api-polling-mention-editor"
          />
        </Grid>

        {/* HTTP Method Section */}
        <Grid item xs={12} md={6} className="trigger-api-polling-section">
          <Typography className="trigger-api-polling-label">HTTP Method</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={httpMethod}
              onChange={handleHttpMethodChange}
              className="trigger-api-polling-select"
            >
              {HTTP_METHODS.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Response Format Section */}
        <Grid item xs={12} md={6} className="trigger-api-polling-section">
          <Typography className="trigger-api-polling-label">Response Format</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={data.responseFormat || RESPONSE_FORMAT_JSON}
              onChange={handleResponseFormatChange}
              className="trigger-api-polling-select"
            >
              {RESPONSE_FORMATS.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Polling Interval Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <Typography className="trigger-api-polling-label">Polling Interval</Typography>
          <Typography className="trigger-api-polling-helper-text">
            How often should this endpoint be polled?
          </Typography>
          <div className="trigger-api-polling-interval-row">
            <TextField
              type="number"
              variant="outlined"
              size="small"
              value={data.pollingInterval?.value || 5}
              onChange={handleIntervalValueChange}
              inputProps={{ min: 1 }}
              className="trigger-api-polling-interval-value"
            />
            <FormControl size="small" className="trigger-api-polling-interval-type">
              <Select
                value={data.pollingInterval?.type || INTERVAL_TYPE_MINUTES}
                onChange={handleIntervalTypeChange}
              >
                {INTERVAL_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        {/* Success Status Codes Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <Typography className="trigger-api-polling-label">Success Status Codes</Typography>
          <Typography className="trigger-api-polling-helper-text">
            The workflow will trigger when the API returns one of these status codes.
          </Typography>
          <Autocomplete
            multiple
            options={COMMON_STATUS_CODES}
            getOptionLabel={(option) =>
              typeof option === 'object' ? option.label : `${option}`
            }
            value={(data.successStatusCodes || DEFAULT_SUCCESS_CODES).map((code) => {
              const found = COMMON_STATUS_CODES.find((sc) => sc.code === code);
              return found || { code, label: `${code}` };
            })}
            onChange={handleStatusCodesChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={typeof option === 'object' ? option.code : option}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Add status code"
              />
            )}
            freeSolo
            className="trigger-api-polling-autocomplete"
          />
        </Grid>

        {/* Headers Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <div className="trigger-api-polling-section-header">
            <Typography className="trigger-api-polling-label">HTTP Headers</Typography>
            <IconButton
              size="small"
              onClick={handleAddHeader}
              className="trigger-api-polling-add-btn"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
          <Typography className="trigger-api-polling-helper-text">
            Add custom headers like Content-Type, Authorization, etc.
          </Typography>
          {(data.headers || []).map((header, index) => (
            <div key={index} className="trigger-api-polling-kv-row">
              <TextField
                variant="outlined"
                size="small"
                placeholder="Header name"
                value={header.key || ''}
                onChange={(e) => handleUpdateHeader(index, 'key', e.target.value)}
                className="trigger-api-polling-kv-key"
              />
              <div className="trigger-api-polling-kv-value">
                <MentionEditor
                  availablePlaceholders={getEnhancedPlaceholders()}
                  text={header.value || ''}
                  placeholder="Header value (type $ for secrets)"
                  onChange={(value) => handleUpdateHeader(index, 'value', value)}
                  onFocus={() => {}}
                  error={false}
                />
              </div>
              <IconButton size="small" onClick={() => handleRemoveHeader(index)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </Grid>

        {/* Query Parameters Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <div className="trigger-api-polling-section-header">
            <Typography className="trigger-api-polling-label">Query Parameters</Typography>
            <IconButton
              size="small"
              onClick={handleAddQueryParam}
              className="trigger-api-polling-add-btn"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
          <Typography className="trigger-api-polling-helper-text">
            Add query parameters to append to the URL.
          </Typography>
          {(data.queryParams || []).map((param, index) => (
            <div key={index} className="trigger-api-polling-kv-row">
              <TextField
                variant="outlined"
                size="small"
                placeholder="Parameter name"
                value={param.key || ''}
                onChange={(e) => handleUpdateQueryParam(index, 'key', e.target.value)}
                className="trigger-api-polling-kv-key"
              />
              <div className="trigger-api-polling-kv-value">
                <MentionEditor
                  availablePlaceholders={getEnhancedPlaceholders()}
                  text={param.value || ''}
                  placeholder="Parameter value (type $ for secrets)"
                  onChange={(value) => handleUpdateQueryParam(index, 'value', value)}
                  onFocus={() => {}}
                  error={false}
                />
              </div>
              <IconButton size="small" onClick={() => handleRemoveQueryParam(index)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </Grid>

        {/* Request Body Section (only for POST/PUT) */}
        {showRequestBody && (
          <Grid item xs={12} className="trigger-api-polling-section">
            <Typography className="trigger-api-polling-label">Request Body</Typography>
            <Typography className="trigger-api-polling-helper-text">
              Enter the request body in JSON or XML format. Type "$" to insert placeholders or
              secrets.
            </Typography>
            <div className="trigger-api-polling-body-editor">
              <MentionEditor
                availablePlaceholders={getEnhancedPlaceholders()}
                text={data.requestBody || ''}
                placeholder='{"key": "value"}'
                onChange={handleRequestBodyChange}
                onFocus={() => {}}
                error={false}
                minHeight={150}
              />
            </div>
          </Grid>
        )}

        {/* Output Placeholder Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <Typography className="trigger-api-polling-label">Output Placeholder</Typography>
          <Typography className="trigger-api-polling-helper-text">
            The API response will be stored in this placeholder as a JSON object.
          </Typography>
          <W86PlaceholderTextField
            value={data.outputKey || ''}
            error={checkKeyError}
            helperText={checkKeyError ? errorMessage : null}
            placeholderText="Enter placeholder for response"
            handleChange={handleKeyChange}
            customStyleProps={{ maxWidth: 400 }}
          />
        </Grid>

        {/* Poll History Section */}
        <Grid item xs={12} className="trigger-api-polling-section">
          <div className="trigger-api-polling-section-header">
            <Typography className="trigger-api-polling-label">Recent Poll Activity</Typography>
            <IconButton
              size="small"
              onClick={fetchPollHistory}
              disabled={pollHistoryLoading}
              className="trigger-api-polling-refresh-btn"
              title="Refresh poll history"
            >
              {pollHistoryLoading ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </div>
          <Typography className="trigger-api-polling-helper-text">
            Shows the most recent poll attempts and their results.
          </Typography>

          {pollHistoryLoading && pollHistory.length === 0 ? (
            <div className="trigger-api-polling-history-loading">
              <CircularProgress size={20} />
              <span>Loading poll history...</span>
            </div>
          ) : pollHistory.length === 0 ? (
            <div className="trigger-api-polling-history-empty">
              No poll history yet. History will appear here once the workflow is published and
              polling begins.
            </div>
          ) : (
            <div className="trigger-api-polling-history-list">
              {pollHistory.slice(0, 20).map((entry, index) => (
                <div
                  key={index}
                  className={`trigger-api-polling-history-entry-container ${
                    entry.success ? 'success' : 'failure'
                  }`}
                >
                  <div className="trigger-api-polling-history-entry">
                    <div className="trigger-api-polling-history-icon">
                      {entry.success ? (
                        <CheckCircleIcon fontSize="small" className="success-icon" />
                      ) : (
                        <ErrorIcon fontSize="small" className="error-icon" />
                      )}
                    </div>
                    <div className="trigger-api-polling-history-details">
                      <span className="trigger-api-polling-history-time">
                        {formatPollTime(entry.timestamp)}
                      </span>
                      <span className="trigger-api-polling-history-status">
                        {entry.statusCode ? `Status ${entry.statusCode}` : 'No response'}
                      </span>
                      {entry.error && (
                        <span className="trigger-api-polling-history-error">{entry.error}</span>
                      )}
                    </div>
                    <div className="trigger-api-polling-history-result">
                      {entry.success ? 'Success' : 'Not matched'}
                    </div>
                    {entry.responseBody && (
                      <IconButton
                        size="small"
                        onClick={() => toggleHistoryEntryExpanded(index)}
                        className="trigger-api-polling-history-expand-btn"
                        title={
                          expandedHistoryEntries[index] ? 'Hide response' : 'Show response'
                        }
                      >
                        {expandedHistoryEntries[index] ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </div>
                  {entry.responseBody && expandedHistoryEntries[index] && (
                    <div className="trigger-api-polling-history-response">
                      <div className="trigger-api-polling-history-response-header">
                        <span>Response Body</span>
                        {entry.responseBodyTruncated && (
                          <span className="truncated-badge">Truncated</span>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => copyResponseToClipboard(entry.responseBody)}
                          title="Copy response"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </div>
                      <pre className="trigger-api-polling-history-response-body">
                        {formatResponseBody(entry.responseBody)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Grid>
      </Grid>
    </CustomPaper>
  );
};

export default TriggerFromApiPollingTemplate;
